import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
	CardElement,
	Elements,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import {
	Container,
	Paper,
	Typography,
	Box,
	Button,
	Card,
	CardContent,
	Divider,
	CircularProgress,
	Alert,
	Grid,
	IconButton,
	AppBar,
	Toolbar,
	useTheme,
	useMediaQuery,
} from "@mui/material";
import {
	Add,
	Remove,
	CreditCard,
	CheckCircle,
	TrendingUp,
	Search,
	Menu as MenuIcon,
} from "@mui/icons-material";
import axios from "axios";

// TODO: Replace with your actual Stripe publishable key
// Get this from your Stripe Dashboard: https://dashboard.stripe.com/apikeys
const stripePromise = loadStripe(
	process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_YOUR_PUBLISHABLE_KEY"
);

const PAYMENT_OPTIONS = [
	{
		id: "leads",
		title: "Personalized Leads",
		description: "Get 100 high-quality, personalized leads",
		price: 10,
		unit: "per 100 leads",
	},
	{
		id: "appointments",
		title: "Appointment Setup",
		description: "Get appointments scheduled with qualified prospects",
		price: 200,
		unit: "per appointment",
	},
];

const PaymentForm = ({ quantities, total, onSuccess, onError, userId, email }) => {
	const stripe = useStripe();
	const elements = useElements();
	const [processing, setProcessing] = useState(false);
	const [error, setError] = useState(null);

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		setProcessing(true);
		setError(null);

		try {
			// TODO: Replace with your backend endpoint
			// This endpoint should create a PaymentIntent and return client_secret
			// Example backend endpoint: POST /api/create-payment-intent
			const response = await axios.post(
				process.env.REACT_APP_PAYMENT_API || "/api/create-payment-intent",
				{
					amount: total * 100, // Convert to cents
					currency: "usd",
					app: "introflow",
					userId: userId,
					email: email,
					items: Object.entries(quantities)
						.filter(([_, qty]) => qty > 0)
						.map(([id, qty]) => ({
							optionId: id,
							quantity: qty,
							price: PAYMENT_OPTIONS.find((opt) => opt.id === id)?.price || 0,
						})),
				}
			);

			const { clientSecret } = response.data;

			// Confirm payment with Stripe
			const { error: confirmError, paymentIntent } =
				await stripe.confirmCardPayment(clientSecret, {
					payment_method: {
						card: elements.getElement(CardElement),
					},
				});

			if (confirmError) {
				setError(confirmError.message);
				onError(confirmError.message);
			} else if (paymentIntent.status === "succeeded") {
				// Payment succeeded - confirm with backend immediately
				try {
					// Call confirm-payment endpoint to:
					// 1. Store the order in database
					// 2. Trigger fulfillment (send leads, schedule appointments)
					// 3. Send receipt email
					// 4. Update user account
					await axios.post(
						process.env.REACT_APP_PAYMENT_API || "/api/confirm-payment",
						{
							paymentIntentId: paymentIntent.id,
							amount: paymentIntent.amount,
							currency: paymentIntent.currency || "usd",
							app: "introflow",
							userId: userId,
							email: email,
							items: Object.entries(quantities)
								.filter(([_, qty]) => qty > 0)
								.map(([id, qty]) => ({
									optionId: id,
									quantity: qty,
									price: PAYMENT_OPTIONS.find((opt) => opt.id === id)?.price || 0,
								})),
						}
					);
					// Backend confirmed successfully - proceed to success handler
					onSuccess(paymentIntent);
				} catch (confirmErr) {
					// Payment succeeded but backend confirmation failed
					// Log error for debugging
					console.error("Payment succeeded but backend confirmation failed:", confirmErr);
					
					// Still show success to user since payment was successful
					// Backend can catch up via webhook if configured
					onSuccess(paymentIntent);
					
					// Optionally show a warning to user
					// setError("Payment successful, but order confirmation is pending. Please contact support if you don't receive a confirmation email.");
				}
			}
		} catch (err) {
			const errorMessage =
				err.response?.data?.message || err.message || "Payment failed";
			setError(errorMessage);
			onError(errorMessage);
		} finally {
			setProcessing(false);
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Box sx={{ mb: 3 }}>
				<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
					Card Information
				</Typography>
				<Paper
					variant="outlined"
					sx={{
						p: 2,
						backgroundColor: "#f9fafb",
					}}
				>
					<CardElement
						options={{
							style: {
								base: {
									fontSize: "16px",
									color: "#424770",
									fontFamily: "system-ui, sans-serif",
									"::placeholder": {
										color: "#aab7c4",
									},
								},
								invalid: {
									color: "#9e2146",
								},
							},
						}}
					/>
				</Paper>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Button
				type="submit"
				variant="contained"
				color="primary"
				fullWidth
				size="large"
				disabled={!stripe || processing || total === 0}
				startIcon={processing ? <CircularProgress size={20} /> : <CreditCard />}
				sx={{
					py: 1.5,
					fontSize: "1.1rem",
					fontWeight: 600,
					textTransform: "none",
				}}
			>
				{processing ? "Processing..." : `Pay $${total.toFixed(2)}`}
			</Button>
		</form>
	);
};

const PayForOutcome = () => {
	const auth = useAuth();
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [quantities, setQuantities] = useState({
		leads: 0,
		appointments: 0,
	});
	const [paymentSuccess, setPaymentSuccess] = useState(false);
	const [paymentError, setPaymentError] = useState(null);

	const handleLoginClick = () => {
		auth.signinRedirect();
	};

	// Get user ID and email from auth
	// Try multiple possible fields for user ID (OIDC standard uses 'sub')
	const userId = auth.user?.profile?.sub || 
	               auth.user?.profile?.id || 
	               auth.user?.id || 
	               null;
	const email = auth.user?.profile?.email || 
	              auth.user?.email || 
	              null;

	const handleQuantityChange = (optionId, delta) => {
		setQuantities((prev) => ({
			...prev,
			[optionId]: Math.max(0, (prev[optionId] || 0) + delta),
		}));
	};

	const total = Object.entries(quantities).reduce((sum, [id, qty]) => {
		const option = PAYMENT_OPTIONS.find((opt) => opt.id === id);
		return sum + (option ? option.price * qty : 0);
	}, 0);

	const handlePaymentSuccess = (paymentIntent) => {
		setPaymentSuccess(true);
		setPaymentError(null);
		
		// Payment Intent contains:
		// - id: payment intent ID (e.g., "pi_xxxxx")
		// - amount: amount in cents
		// - status: "succeeded"
		// - created: timestamp
		// - metadata: custom data you can add
		
		// Note: /api/confirm-payment is now called in the payment flow
		// (in PaymentForm handleSubmit) right after payment succeeds
		// This ensures backend is notified immediately for order fulfillment
	};

	const handlePaymentError = (error) => {
		setPaymentError(error);
		setPaymentSuccess(false);
	};

	return (
		<div className="bg-gray-50 min-h-screen">
			{/* Navigation Bar */}
			<AppBar
				position="sticky"
				elevation={0}
				color="default"
				sx={{ backgroundColor: "white", borderBottom: "1px solid #e5e7eb" }}
			>
				<Container maxWidth="lg">
					<Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
						<Typography
							variant="h6"
							component="div"
							sx={{
								color: theme.palette.primary.main,
								fontWeight: 700,
								display: "flex",
								alignItems: "center",
								cursor: "pointer",
							}}
							onClick={() => navigate("/")}
						>
							<Search sx={{ mr: 1 }} />
							introflow
						</Typography>

						{isMobile ? (
							<IconButton
								edge="end"
								color="inherit"
								aria-label="menu"
							>
								<MenuIcon />
							</IconButton>
						) : (
							<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
								<Button 
									color="inherit" 
									onClick={() => {
										navigate("/");
										// Scroll to products section after navigation
										setTimeout(() => {
											const productsSection = document.getElementById("products");
											if (productsSection) {
												productsSection.scrollIntoView({ behavior: "smooth" });
											}
										}, 100);
									}}
									sx={{ color: "text.primary", textTransform: "none" }}
								>
									Products
								</Button>
								<Button 
									color="inherit" 
									onClick={() => navigate("/pay-for-outcome")}
									sx={{ 
										color: "primary.main", 
										textTransform: "none",
										fontWeight: 600,
									}}
								>
									Pricing
								</Button>
								<Button
									variant="contained"
									color="primary"
									onClick={handleLoginClick}
									sx={{ textTransform: "none" }}
								>
									Login
								</Button>
							</Box>
						)}
					</Toolbar>
				</Container>
			</AppBar>

			<Container maxWidth="md" sx={{ py: 6 }}>
				{/* Header */}
			<Box sx={{ textAlign: "center", mb: 6 }}>
				<Typography
					variant="h3"
					component="h1"
					sx={{ fontWeight: 700, mb: 2, color: "text.primary" }}
				>
					Pay for outcome, not marketing hype.
				</Typography>
				<Typography
					variant="h6"
					sx={{ color: "text.secondary", maxWidth: 600, mx: "auto" }}
				>
					Get real results with our outcome-based pricing model. Only pay when you
					get value.
				</Typography>
			</Box>

			{paymentSuccess ? (
				<Paper
					elevation={3}
					sx={{
						p: 4,
						textAlign: "center",
						backgroundColor: "#f0fdf4",
					}}
				>
					<CheckCircle sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
					<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
						Payment Successful!
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
						Your order has been processed. You will receive a confirmation email
						shortly with your receipt and order details.
					</Typography>
					<Box sx={{ mt: 3 }}>
						<Button
							variant="outlined"
							onClick={() => {
								setPaymentSuccess(false);
								setQuantities({ leads: 0, appointments: 0 });
							}}
							sx={{ mr: 2 }}
						>
							Place Another Order
						</Button>
						<Button
							variant="contained"
							onClick={() => window.print()}
						>
							Print Receipt
						</Button>
					</Box>
				</Paper>
			) : (
				<Grid container spacing={4}>
					{/* Options Selection */}
					<Grid item xs={12} md={7}>
						<Paper elevation={3} sx={{ p: 3 }}>
							<Typography
								variant="h5"
								sx={{ fontWeight: 600, mb: 3, display: "flex", alignItems: "center" }}
							>
								<TrendingUp sx={{ mr: 1 }} />
								Select Your Options
							</Typography>

							{PAYMENT_OPTIONS.map((option, index) => (
								<Box key={option.id}>
									<Card
										variant="outlined"
										sx={{
											mb: 3,
											borderColor:
												quantities[option.id] > 0
													? "primary.main"
													: "divider",
											borderWidth: quantities[option.id] > 0 ? 2 : 1,
										}}
									>
										<CardContent>
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "flex-start",
													mb: 2,
												}}
											>
												<Box sx={{ flex: 1 }}>
													<Typography
														variant="h6"
														sx={{ fontWeight: 600, mb: 0.5 }}
													>
														{option.title}
													</Typography>
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{ mb: 1 }}
													>
														{option.description}
													</Typography>
													<Typography
														variant="h6"
														sx={{ color: "primary.main", fontWeight: 600 }}
													>
														${option.price} {option.unit}
													</Typography>
												</Box>
											</Box>

											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
												}}
											>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													Quantity:
												</Typography>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
													}}
												>
													<IconButton
														size="small"
														onClick={() => handleQuantityChange(option.id, -1)}
														disabled={quantities[option.id] === 0}
														sx={{
															border: "1px solid",
															borderColor: "divider",
														}}
													>
														<Remove fontSize="small" />
													</IconButton>
													<Typography
														variant="h6"
														sx={{
															minWidth: 40,
															textAlign: "center",
															fontWeight: 600,
														}}
													>
														{quantities[option.id] || 0}
													</Typography>
													<IconButton
														size="small"
														onClick={() => handleQuantityChange(option.id, 1)}
														sx={{
															border: "1px solid",
															borderColor: "divider",
														}}
													>
														<Add fontSize="small" />
													</IconButton>
												</Box>
											</Box>

											{quantities[option.id] > 0 && (
												<Typography
													variant="body2"
													sx={{
														mt: 1,
														color: "primary.main",
														fontWeight: 500,
													}}
												>
													Subtotal: $
													{(option.price * quantities[option.id]).toFixed(2)}
												</Typography>
											)}
										</CardContent>
									</Card>
									{index < PAYMENT_OPTIONS.length - 1 && (
										<Divider sx={{ mb: 3 }} />
									)}
								</Box>
							))}
						</Paper>
					</Grid>

					{/* Payment Summary & Form */}
					<Grid item xs={12} md={5}>
						<Paper elevation={3} sx={{ p: 3, position: "sticky", top: 20 }}>
							<Typography
								variant="h6"
								sx={{ fontWeight: 600, mb: 3 }}
							>
								Order Summary
							</Typography>

							<Box sx={{ mb: 3 }}>
								{Object.entries(quantities)
									.filter(([_, qty]) => qty > 0)
									.map(([id, qty]) => {
										const option = PAYMENT_OPTIONS.find(
											(opt) => opt.id === id
										);
										if (!option) return null;
										return (
											<Box
												key={id}
												sx={{
													display: "flex",
													justifyContent: "space-between",
													mb: 1,
												}}
											>
												<Typography variant="body2">
													{option.title} × {qty}
												</Typography>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													${(option.price * qty).toFixed(2)}
												</Typography>
											</Box>
										);
									})}
								{total === 0 && (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ fontStyle: "italic" }}
									>
										No items selected
									</Typography>
								)}
							</Box>

							<Divider sx={{ my: 3 }} />

							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									mb: 3,
								}}
							>
								<Typography variant="h6" sx={{ fontWeight: 600 }}>
									Total:
								</Typography>
								<Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
									${total.toFixed(2)}
								</Typography>
							</Box>

							{paymentError && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{paymentError}
								</Alert>
							)}

							{total > 0 && (
								<Elements stripe={stripePromise}>
									<PaymentForm
										quantities={quantities}
										total={total}
										onSuccess={handlePaymentSuccess}
										onError={handlePaymentError}
										userId={userId}
										email={email}
									/>
								</Elements>
							)}
						</Paper>
					</Grid>
				</Grid>
			)}
			</Container>
		</div>
	);
};

export default PayForOutcome;
